import _ from "lodash";
import React, { useState, forwardRef } from "react";

import FormField from "./Form.Field";
import FormLabel from "./Form.Label";
import FormHelp from "./Form.Help";
import FormWrapper from "./Form.Wrapper";
import FormValidation from "./Form.Validation";

import * as inputStyles from "./InputField.styles";
import * as styles from "./Select.styles";

type OptionsItemType = {
  id?: any;
  value: any;
  description: React.ReactNode | string;
} & React.OptionHTMLAttributes<HTMLOptionElement>;

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
  id?: string;
  label?: React.ReactNode;
  help?: React.ReactNode;
  floating?: boolean;
  inputLeft?: React.ReactNode;
  inputRight?: React.ReactNode;
  fieldProps?: object;
  value?: string;
  defaultValue?: string;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  isInvalid?: boolean;
  isValid?: boolean;
  errorMessage?: string;
  name?: string;
  placeholder?: string;
  options?: OptionsItemType[];
  withoutLabel?: boolean;
}


const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  id,
  label,
  help,
  floating = true,
  inputLeft,
  inputRight,
  fieldProps = {},
  value,
  defaultValue,
  onFocus,
  onBlur,
  isInvalid = false,
  isValid = false,
  errorMessage,
  name,
  placeholder,
  options = [],
  withoutLabel = false,
  ...selectProps
}, ref) => {

  const [ isFocused, setIsFocused ] = useState(false);

  const handleFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const isControlled = value !== undefined;
  const fieldId = id || `select-${name || "default"}`;
  const hasValueOrFocused = isControlled ?
    !!value || isFocused :
    !!defaultValue || isFocused;

  return (
    <FormField
      className="field-select"
      {...fieldProps}>

      <FormWrapper
        floating={floating}
        isInvalid={isInvalid}
        isValid={isValid}
        hasValueOrFocused={hasValueOrFocused}
        hasFocused={isFocused}
        inputLeft={inputLeft}
        inputRight={inputRight}>

        {!withoutLabel && label && (
          <FormLabel
            htmlFor={fieldId}
            css={styles.label}>
            {label}
          </FormLabel>
        )}

        <select
          ref={ref}
          id={fieldId}
          name={name}
          onFocus={handleFocus}
          onBlur={handleBlur}
          css={[
            inputStyles.base,
            styles.base,
            withoutLabel && styles.withoutLabel,
            isInvalid && inputStyles.error,
            isValid && inputStyles.success
          ]}
          {...(isControlled ? { value } : { defaultValue })}
          {...selectProps}>

          {placeholder && (
            <option value="">
              {isFocused ? placeholder : ""}
            </option>
          )}

          {
            Array.isArray(options) && options.map(({
              id: optionId,
              value: optionValue,
              description,
              ...rest
            }, idx) => (
              <option
                key={optionId ?? optionValue ?? idx}
                id={optionId ?? optionValue ?? idx}
                value={optionValue}
                {...rest}>
                {description}
              </option>
            ))
          }
        </select>

        {!!inputRight && inputRight}

      </FormWrapper>

      {help && (
        <FormHelp>{help}</FormHelp>
      )}

      <FormValidation
        errorMessage={errorMessage || ""}
        isVisible={isInvalid} />

    </FormField>
  );
});

Select.displayName = "Select";
export default Select;
